/**
 * C 端 API 客户端
 */
import { useClientAuth, type ClientUser } from './auth';

function withAuth(init: RequestInit = {}): RequestInit {
  const headers = new Headers(init.headers);
  const token = useClientAuth.getState().token;
  if (token) headers.set('authorization', `Bearer ${token}`);
  return { ...init, headers };
}

async function authenticatedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const hadToken = !!useClientAuth.getState().token;
  const response = await fetch(path, withAuth(init));
  if (hadToken && response.status === 401) useClientAuth.getState().clear();
  return response;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: ClientUser;
}

export async function loginApi(username: string, password: string): Promise<LoginResponse> {
  const response = await fetch('/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  if (!response.ok) {
    if (response.status === 401) throw new Error('账号或密码错误');
    if (response.status === 403) throw new Error('账号已停用，请联系管理员');
    throw new Error('登录失败，请稍后重试');
  }
  return response.json();
}

export async function registerApi(
  username: string,
  nickname: string,
  password: string,
): Promise<LoginResponse> {
  const response = await fetch('/auth/register', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      username,
      password,
      ...(nickname.trim() ? { nickname: nickname.trim() } : {}),
    }),
  });
  if (!response.ok) {
    if (response.status === 409) throw new Error('这个用户名已被注册');
    throw await apiError(response, '注册失败');
  }
  return response.json();
}

export interface ChunkHit {
  id: string;
  score: number;
  text: string;
  book_id: string;
  book_title: string;
  chapter_id: string;
  chapter_title?: string;
  page?: number;
}

export interface SpeciesRef {
  id: string;
  name: string;
  category?: string;
  cover_url?: string;
}

export interface SpeciesDetail {
  id: string;
  name: string;
  scientific?: string;
  aliases: string[];
  category?: string;
  intro?: string;
  cover_url?: string;
  regions: string[];
  production_areas: string[];
  waterways: string[];
  features: string[];
  habits: string[];
  catalog_number?: number;
  volume?: string;
  source_page?: number;
  taxonomy?: string;
  role?: string;
  distribution?: string;
  farming_mode?: string;
  development?: string;
  images: { id: string; url: string; caption?: string }[];
  books: BookCard[];
  sources: {
    chunk_id: string;
    book_id: string;
    book_title: string;
    chapter_title?: string;
    page?: number;
    text: string;
  }[];
}

export interface SearchResponse {
  catalogue?: {total:number;offset:number;returned:number;next_offset:number|null;scope:string;filters:SearchFilters};
  trace_id?: string;
  chunks: ChunkHit[];
  nodes: SpeciesRef[];
  images: { id: string; url: string; caption?: string }[];
  excerpts?: { chunk_id: string; title: string; text: string; focused: boolean }[];
}

type TrackEventFields = {
  visitor_id?: string;
  session_id?: string;
  book_id?: string;
  species_id?: string;
  source?: string;
  path?: string;
  metadata?: Record<string, unknown>;
};

let cachedVisitorId: string | undefined;
function getVisitorId(): string | undefined {
  if (cachedVisitorId) return cachedVisitorId;
  try {
    const key = 'kpqd_visitor_id';
    const existing = window.localStorage.getItem(key);
    if (existing) return (cachedVisitorId = existing);
    const created = typeof crypto?.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(key, created);
    return (cachedVisitorId = created);
  } catch {
    return undefined;
  }
}

/** 埋点失败不应阻断搜索、问答或购书主流程。 */
export function trackEvent(event_type: string, fields: TrackEventFields = {}) {
  try {
    void fetch('/api/v1/events', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ event_type, visitor_id: getVisitorId(), ...fields }),
    }).catch(() => {});
  } catch { /* 浏览器禁用存储或 fetch 时静默跳过 */ }
}

/**
 * C 端检索筛选条件。所有筛选值通过独立字段提交，避免污染用户的自然语言问题。
 * `feature` 与 `habit` 预留给检索服务的种质属性过滤能力。
 */
export interface SearchFilters {
  category?: string;
  region?: string;
  production_area?: string;
  waterway?: string;
  feature?: string;
  habit?: string;
}

export async function searchApi(query: string, filters: SearchFilters = {}): Promise<SearchResponse> {
  const resp = await fetch('/api/v1/search', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ query, filters, top_k: 5, need_images: true }),
  });
  if (!resp.ok) throw new Error(`search ${resp.status}`);
  return resp.json();
}

export interface AskCallbacks {
  onMeta?: (m: any) => void;
  onToken?: (t: string) => void;
  onCitation?: (c: any) => void;
  onImage?: (i: any) => void;
  onDone?: (d: any) => void;
  onError?: (e: { code: string; message: string }) => void;
}

async function apiError(resp: Response, operation: string): Promise<Error> {
  let message = `${operation} ${resp.status}`;
  try {
    const body = await resp.json() as { message?: unknown };
    if (typeof body?.message === 'string' && body.message.trim()) message = body.message;
  } catch { /* Keep the status-based fallback for non-JSON gateway responses. */ }
  return new Error(message);
}

/**
 * 调 /api/v1/ask，按 SSE 帧回调
 */
export async function askApi(message: string, sessionId: string | undefined, cb: AskCallbacks, retry = false, filters?: SearchFilters) {
  const resp = await authenticatedFetch('/api/v1/ask', {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
    body: JSON.stringify({ message, session_id: sessionId, need_image: true, retry, filters }),
  });
  if (!resp.ok) throw await apiError(resp, 'ask');
  if (!resp.body) throw new Error('问答响应为空，请稍后再试');

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });

    // 按 SSE 帧切（双换行）
    let idx;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      parseFrame(frame, cb);
    }
  }
  if (buf.trim()) parseFrame(buf, cb);
}

function parseFrame(frame: string, cb: AskCallbacks) {
  let event = 'message';
  let data = '';
  for (const line of frame.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
  }
  let obj: any = null;
  try { obj = JSON.parse(data); } catch { obj = data; }
  switch (event) {
    case 'meta':     cb.onMeta?.(obj); break;
    case 'token':    cb.onToken?.(obj?.delta ?? ''); break;
    case 'citation': cb.onCitation?.(obj); break;
    case 'image':    cb.onImage?.(obj); break;
    case 'done':     cb.onDone?.(obj); break;
    case 'error':    cb.onError?.(obj); break;
  }
}

export async function getBookApi(id: string) {
  const r = await fetch(`/api/v1/books/${id}`);
  if (!r.ok) throw new Error(`book ${r.status}`);
  return r.json();
}

/** 只有已配置并已发布的图书会出现在此列表；不暴露源 PDF 路径。 */
export async function listSourcePreviewBooksApi(): Promise<string[]> {
  const response = await fetch('/api/v1/source-previews/books');
  if (!response.ok) throw await apiError(response, 'source previews');
  const ids: unknown = await response.json();
  if (!Array.isArray(ids)) throw new Error('原文页配置响应无效');
  return ids.filter((id): id is string => typeof id === 'string');
}

/** 仅返回这本图书确有资料引用的 PDF 物理页。 */
export async function listSourcePreviewPagesApi(bookId: string): Promise<number[]> {
  const response = await fetch(`/api/v1/books/${encodeURIComponent(bookId)}/source-pages`);
  if (!response.ok) throw await apiError(response, 'source pages');
  const pages: unknown = await response.json();
  if (!Array.isArray(pages)) throw new Error('原文页列表响应无效');
  return pages.filter((page): page is number => Number.isInteger(page) && page > 0 && page <= 2147483647);
}

/** Fetch the protected single-page PDF as data, never as a browser navigation/download. */
export async function getSourcePagePdfApi(bookId: string, page: number, signal?: AbortSignal): Promise<Uint8Array> {
  const response = await authenticatedFetch(
    `/api/v1/books/${encodeURIComponent(bookId)}/source-pages/${page}`,
    { signal },
  );
  if (!response.ok) {
    if (response.status === 404) throw new Error('这一页原文暂不可查看');
    if (response.status === 401 || response.status === 403) throw new Error('请登录后查看原文页');
    throw await apiError(response, '读取原文页失败');
  }
  if (!response.headers.get('content-type')?.includes('application/pdf')) throw new Error('原文页响应格式无效，请稍后重试');
  return new Uint8Array(await response.arrayBuffer());
}

export interface BookSummary {
  id: string;
  title: string;
  subtitle?: string;
  authors: string[];
  publisher?: string;
  cover_url?: string;
  status: string;
}

export async function listBooksApi(atlasOnly = false): Promise<BookSummary[]> {
  const params = new URLSearchParams();
  if (atlasOnly) params.set('atlas', 'true');
  const r = await fetch(`/api/v1/books?${params.toString()}`);
  if (!r.ok) throw new Error(`books ${r.status}`);
  return r.json();
}

export interface ChapterNode {
  id: string;
  parent_id?: string | null;
  order_no: number;
  title: string;
  page_start?: number | null;
  page_end?: number | null;
}

export interface ChapterSourceIndex {
  book_id: string;
  chapter_id: string;
  title: string;
  page_start?: number | null;
  page_end?: number | null;
  pages: import('./lib/chapterSourceNav').ChapterPage[];
}

export async function getChapterSourcePagesApi(bookId: string, chapterId: string, signal?: AbortSignal): Promise<ChapterSourceIndex> {
  const response = await fetch(`/api/v1/books/${encodeURIComponent(bookId)}/chapters/${encodeURIComponent(chapterId)}/source-pages`, { signal });
  if (!response.ok) throw new Error(response.status === 404 ? '此章节原文暂不可查看' : '章节目录加载失败，请重试');
  return response.json();
}

/** 图书章节目录（真实数据，含页码区间）。 */
export async function getChaptersApi(id: string): Promise<ChapterNode[]> {
  const r = await fetch(`/api/v1/books/${id}/chapters`);
  if (!r.ok) throw new Error(`chapters ${r.status}`);
  return r.json();
}

/** 该图书提及的核心品种（图谱 MENTIONED_IN 派生）。 */
export async function getBookSpeciesApi(id: string): Promise<SpeciesRef[]> {
  const r = await fetch(`/api/v1/books/${id}/species`);
  if (!r.ok) throw new Error(`book species ${r.status}`);
  return r.json();
}

export async function getNeighborsApi(id: string, depth = 1) {
  const r = await fetch(`/api/v1/graph/neighbors/${id}?depth=${depth}`);
  if (!r.ok) throw new Error(`neighbors ${r.status}`);
  return r.json();
}

export async function getSpeciesDetailApi(id: string): Promise<SpeciesDetail> {
  const r = await fetch(`/api/v1/species/${id}`);
  if (!r.ok) throw new Error(`species detail ${r.status}`);
  return r.json();
}

export async function speciesSearchApi(
  q: string,
  limit = 50,
  offset = 0,
  category?: string,
  source?: string,
  atlas = false,
) {
  const params = new URLSearchParams({
    q,
    limit: String(Math.min(Math.max(limit, 1), 50)),
    offset: String(Math.max(offset, 0)),
  });
  if (category) params.set('category', category);
  if (source) params.set('source', source);
  if (atlas) params.set('atlas', 'true');
  const r = await fetch(`/api/v1/species/search?${params.toString()}`);
  if (!r.ok) throw new Error(`species ${r.status}`);
  return r.json();
}

export interface AtlasSummary {
  total: number;
  books: number;
  categories: { name: string; count: number }[];
}

export async function getAtlasSummaryApi(): Promise<AtlasSummary> {
  const r = await fetch('/api/v1/atlas/summary');
  if (!r.ok) throw new Error(`atlas summary ${r.status}`);
  return r.json();
}

export interface SessionItem {
  id: string;
  title: string;
  turns: number;
  updated_at: string;
}

export async function getSessionsApi(): Promise<SessionItem[]> {
  const r = await authenticatedFetch('/api/v1/sessions');
  if (!r.ok) throw new Error(`sessions ${r.status}`);
  return r.json();
}

export interface SessionTurn {
  order_no: number;
  role: string;       // 'user' | 'assistant'
  content: string;
  intent?: Record<string, unknown> | null;
  citations?: Array<{
    chunk_id?: string;
    source_url?: string;
    focus_fields?: string[];
    index?: number;
    n?: number;
    book_id?: string;
    book_title?: string;
    chapter_title?: string;
    page?: number;
    text?: string;
    score?: number;
  }>;
  images?: Array<{ url?: string; caption?: string; emoji?: string }>;
  enrichments?: {
    books?: BookCard[];
    followups?: string[];
    clarifications?: string[];
    generation_error?: { code?: string; message: string };
    related_species?: SpeciesRef[];
  };
  usage?: Record<string, unknown> | null;
  route?: string;
  created_at: string;
}

export interface BookCard {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
  purchase_url?: string;
}

/** 取某会话的全部轮次（用于恢复历史对话）。 */
export async function getSessionApi(id: string): Promise<SessionTurn[]> {
  const r = await authenticatedFetch(`/api/v1/sessions/${id}`);
  if (!r.ok) throw new Error(`session ${r.status}`);
  return r.json();
}

export interface CreatedSessionShare {
  share_id: string;
  token: string;
  expires_at: string;
}

export interface PublicSessionShare {
  title: string;
  expires_at: string;
  turns: Array<Pick<SessionTurn, 'order_no' | 'role' | 'content' | 'citations' | 'images' | 'enrichments' | 'created_at'>>;
}

export interface SessionShareItem {
  id: string;
  expires_at: string;
  revoked_at?: string | null;
  created_at: string;
}

/** 创建 30 天有效的不可变公开快照；服务端仅接受当前会话所有者。 */
export async function createSessionShareApi(id: string): Promise<CreatedSessionShare> {
  const r = await authenticatedFetch(`/api/v1/sessions/${encodeURIComponent(id)}/shares`, { method: 'POST' });
  if (!r.ok) throw new Error(`create share ${r.status}`);
  return r.json();
}

/** 不带登录态读取公开快照。令牌失效、撤销或不存在时统一返回 404。 */
export async function getPublicSessionShareApi(token: string): Promise<PublicSessionShare> {
  const r = await fetch(`/api/v1/shares/${encodeURIComponent(token)}`);
  if (!r.ok) throw new Error(`public share ${r.status}`);
  return r.json();
}

export async function listSessionSharesApi(id: string): Promise<SessionShareItem[]> {
  const r = await authenticatedFetch(`/api/v1/sessions/${encodeURIComponent(id)}/shares`);
  if (!r.ok) throw new Error(`list shares ${r.status}`);
  return r.json();
}

export async function revokeSessionShareApi(sessionId: string, shareId: string): Promise<void> {
  const r = await authenticatedFetch(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/shares/${encodeURIComponent(shareId)}`,
    { method: 'DELETE' },
  );
  if (!r.ok) throw new Error(`revoke share ${r.status}`);
}

export async function deleteSessionApi(id: string): Promise<void> {
  const r = await authenticatedFetch(`/api/v1/sessions/${id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`delete session ${r.status}`);
}

export async function clearSessionsApi(): Promise<void> {
  const r = await authenticatedFetch('/api/v1/sessions', { method: 'DELETE' });
  if (!r.ok) throw new Error(`clear sessions ${r.status}`);
}

export type SearchFacetOptions = Partial<Record<keyof SearchFilters, {value:string;count:number}[]>>;
export async function getSearchFacetsApi():Promise<SearchFacetOptions> {
  const response=await fetch('/api/v1/search/facets');
  if(!response.ok)throw new Error(`facets ${response.status}`);
  return response.json();
}
export interface CitationSourceRecord {chunk_id:string;book_id:string;book_title:string;chapter_id:string;chapter_title?:string;pdf_page:number;text:string;source_url?:string}
export async function getCitationSourceApi(chunkId:string,signal?:AbortSignal):Promise<CitationSourceRecord> {
  const response=await authenticatedFetch(`/api/v1/sources/${encodeURIComponent(chunkId)}`,{signal});
  if(!response.ok)throw await apiError(response,'读取引用原文失败');
  return response.json();
}
